import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Check } from "lucide-react";
import mahirahImg from "@/assets/mahirah-800.webp";
import mahirahImgSmall from "@/assets/mahirah-480.webp";
import { getString, getStringArray } from "@/lib/pageContent";

interface FounderSectionProps {
  content?: Record<string, unknown>;
}

const fallbackContent = {
  eyebrow: "About the Founder",
  title: "Coaching and training built on 15+ years of corporate truth",
  paragraphs: [
    "MVIBE is a coaching, training, and image consulting firm founded by Mahirah (Sana Amrin). With 15+ years of experience in IT HR leadership and corporate operations, Mahirah blends real-world corporate insight with certified coaching methodology to deliver training that actually sticks.",
    "She has spent 300+ hours coaching individuals and groups, and delivered 150+ hours of training in emotional intelligence, leadership, communication, art therapy, graphology, and stress management across corporates, colleges, and hotels.",
  ],
  credentials: [
    "Life Coach | Executive Facilitator | Soft Skills Trainer | Art Therapist | Graphologist",
    "15+ Years of Experience in IT HR Leadership, Corporate Training, and Operations",
    "Professional Certifications: ICF, NLP, ART Therapy, NABET, POSH, City & Guild, Graphology",
  ],
  founderLabel: "Founder – MViBE",
  cofounderLabel: "Co-founder – Bold Bridge",
  ctaLabel: "Learn More About Mahirah →",
};

const FounderSection = ({ content }: FounderSectionProps) => {
  const eyebrow = getString(content?.eyebrow, fallbackContent.eyebrow);
  const title = getString(content?.title, fallbackContent.title);
  const paragraphs = getStringArray(content?.paragraphs, fallbackContent.paragraphs);
  const credentials = getStringArray(content?.credentials, fallbackContent.credentials);
  const ctaLabel = getString(content?.ctaLabel, fallbackContent.ctaLabel);
  const founderLabel = getString(content?.founderLabel, fallbackContent.founderLabel);
  const cofounderLabel = getString(content?.cofounderLabel, fallbackContent.cofounderLabel);

  return (
    <section className="bg-muted/40 py-10">
      <div className="container mx-auto px-4">
        <div className="mx-auto grid max-w-5xl items-center gap-4 md:grid-cols-2">
          <motion.div
            initial={{ opacity: 0, x: -60 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, ease: "easeOut" }}
            className="relative"
          >
            <div className="relative overflow-hidden rounded-xl border-4 border-accent shadow-lg">
              <img
                src={mahirahImg}
                srcSet={`${mahirahImgSmall} 480w, ${mahirahImg} 800w`}
                sizes="(max-width: 768px) 100vw, 400px"
                alt="Mahirah (Sana Amrin) — Founder of MVIBE"
                width={800}
                height={800}
                className="h-auto w-full object-cover"
                loading="lazy"
                decoding="async"
              />
            </div>
            <div className="absolute -bottom-3 -right-3 -z-10 h-20 w-20 rounded-xl bg-primary" />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 60 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, ease: "easeOut", delay: 0.15 }}
          >
            <p className="mb-2 font-sans text-base uppercase tracking-[0.2em] text-primary">{eyebrow}</p>
            <h2 className="text-3xl font-heading font-bold leading-snug text-foreground md:text-5xl">{title}</h2>

            <div className="mt-4 space-y-3">
              {paragraphs.map((paragraph) => (
                <p key={paragraph} className="font-sans text-base leading-relaxed text-muted-foreground">
                  {paragraph}
                </p>
              ))}
            </div>

            <div className="mt-5 space-y-2.5">
              {credentials.map((credential) => (
                <div key={credential} className="flex items-start gap-3">
                  <Check className="mt-0.5 h-5 w-5 shrink-0 text-accent" />
                  <span className="font-sans text-base text-foreground">{credential}</span>
                </div>
              ))}
            </div>

            <div className="mt-4 flex flex-wrap gap-2">
              <span className="rounded-full bg-primary px-3 py-1 text-sm font-bold text-primary-foreground">{founderLabel}</span>
              <span className="rounded-full border-2 border-accent bg-accent/15 px-3 py-1 text-sm font-bold text-accent-foreground">{cofounderLabel}</span>
            </div>

            <Link to="/about" className="mt-5 inline-block text-base font-semibold text-primary hover:underline">
              {ctaLabel}
            </Link>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default FounderSection;
